export const API_ENDPOINTS = {
  AUTH: {
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    VERIFY_EMAIL: '/auth/verify-email',
    RESEND_VERIFICATION: '/auth/resend-verification-token',
    REQUEST_PASSWORD_RESET: '/auth/request-password-reset',
    RESET_PASSWORD: '/auth/reset-password',
    GOOGLE_LOGIN: '/auth/google',
    GOOGLE_CODE_LOGIN: '/auth/google/code',
  },
  LISTINGS: {
    GET_ALL: '/listings',
    CREATE_OR_UPDATE_STEP: '/listings',
    SUBMIT: '/listings/submit',
    GET_BY_ID: (id: string) => `/listings/${id}`,
    PHOTOS_UPLOAD: (id: string) => `/listings/${id}/photos/upload`,
    ADMIN_PHOTOS_UPLOAD: (id: string) => `/listings/${id}/photos/admin-upload`,
    MY_LISTINGS: '/listings/my-listings',
    PUBLISH: (id: string) => `/listings/${id}/publish`,
    DELETE: (id: string) => `/listings/listing/delete/${id}`,
  },
  DEALER: {
    CREATE: '/dealer/register',
    PACKAGES: '/dealer-packages',
    GET_BY_ID: (id: string) => `/dealer/${id}`,
    UPDATE: (id: string) => `/dealer/${id}`,
    PATCH: (id: string) => `/dealer/${id}`,
    SUBMIT: (id: string) => `/dealer/${id}/submit`,
    STATUS: (id: string) => `/dealer/${id}/status`,
    LISTINGS: (id: string) => `/dealer/${id}/listings`,
    ALL_DEALERS: '/admin/dealers',
    ADMIN_CREATE_DEALER: '/admin/dealers',
    ADMIN_UPDATE_DEALER: (id: string) => `/admin/dealers/${id}`,
    APPROVAL_QUEUE: '/admin/dealer-approval/queue',
    APPROVE_DEALER: (id: string) => `/admin/dealer-approval/${id}/approve`,
    REJECT_DEALER: (id: string) => `/admin/dealer-approval/${id}/reject`,
    PAYMENT_APPROVAL_QUEUE: '/admin/dealer-payment/queue',
    APPROVE_PAYMENT: (id: string) => `/admin/dealer-payment/${id}/approve`,
    REJECT_PAYMENT: (id: string) => `/admin/dealer-payment/${id}/reject`,
    PUBLIC_PROFILE: (id: string) => `/dealer/${id}/public`,
    SUBSCRIPTION: (id: string) => `/dealer/${id}/subscription`,
    INVOICES: (id: string) => `/dealer/${id}/invoices`,
  },
  ADMIN: {
    DASHBOARD_STATS: '/admin/dashboard/stats',
    RECENT_ACTIVITY: '/admin/dashboard/recent-activity',
    USERS: '/admin/users',
    USER_BY_ID: (id: string) => `/admin/users/${id}`,
    USER_LISTINGS: (id: string) => `/admin/users/${id}/listings`,
    UPDATE_USER: (id: string) => `/admin/users/${id}`,
    DELETE_USER: (id: string) => `/admin/users/${id}`,
    ADJUST_CREDITS: (id: string) => `/admin/users/${id}/credits`,
    LISTING_DETAILS: (id: string) => `/admin/listings/${id}`,
    UPDATE_LISTING_STATUS: (id: string) => `/admin/listings/${id}/status`,
    DELETE_LISTING: (id: string) => `/admin/listings/${id}/delete`,
    EDIT_LISTING: (id: string) => `/admin/listings/${id}/edit`,
    SEARCH_LISTINGS: '/admin/listings/search',
    UPLOAD_LISTING_PHOTOS: (id: string) => `/admin/listings/${id}/photos/upload`,
    SET_PRIMARY_PHOTO: (listingId: string, photoId: string) => `/admin/listings/${listingId}/photos/${photoId}/set-primary`,
    DELETE_PHOTO: (listingId: string, photoId: string) => `/admin/listings/${listingId}/photos/${photoId}`,
    ADMIN_ACTIONS: '/admin/actions',
    SEND_NOTIFICATION: '/admin/notifications/send',
    NOTIFICATION_STATS: '/admin/notifications/stats',
    // Credit Packages
    CREDIT_PACKAGES: '/admin/credit-packages',
    CREDIT_PACKAGE_BY_ID: (id: string) => `/admin/credit-packages/${id}`,
    // Promotion Packages
    PROMOTION_PACKAGES: '/admin/promotion-packages',
    PROMOTION_PACKAGE_BY_ID: (id: string) => `/admin/promotion-packages/${id}`,
    // Dealer Packages
    DEALER_PACKAGES: '/admin/dealer-packages',
    DEALER_PACKAGE_BY_ID: (id: string) => `/admin/dealer-packages/${id}`,
    // Credit Tracking
    CREDIT_TRANSACTIONS: '/admin/credit-transactions',
    USER_CREDIT_SUMMARY: (userId: string) => `/admin/users/${userId}/credit-summary`,
    CREDIT_ANALYTICS: '/admin/credit-analytics',
  },
  PAYMENTS: {
    GET_ALL: '/payments',
    GET_STATS: '/payments/stats',
    MY_PAYMENTS: '/payments/my-payments',
    INITIATE_CREDITS: '/payments/credits/initiate',
    PURCHASE_CREDITS: '/payments/credits/purchase',
    PURCHASE_CREDITS_CASH: '/payments/credits/purchase-cash',
    INITIATE_DEALER: '/payments/dealer/initiate',
    PURCHASE_DEALER_CASH: '/payments/dealer/purchase-cash',
    SUBSCRIBE_DEALER: '/payments/dealer/subscribe',
    PENDING_CASH_PAYMENTS: '/payments/my-pending-cash',
    CHECK_STATUS: (id: string) => `/payments/${id}/status`,
    COMPLETE: (id: string) => `/payments/${id}/complete`,
    FAIL: (id: string) => `/payments/${id}/fail`,
    REFUND: (id: string) => `/payments/${id}/refund`,
    DELETE: (id: string) => `/payments/${id}`,
  },
  USER_PROFILE: {
    GET_PROFILE: '/users/profile',
    UPDATE_PROFILE: '/users/profile',
    UPDATE_BASIC: '/users/profile/basic',
    UPLOAD_AVATAR: '/users/profile/avatar',
    UPDATE_PASSWORD: '/users/profile/password',
    GET_ADDRESSES: '/users/profile/addresses',
    ADD_ADDRESS: '/users/profile/addresses',
    UPDATE_ADDRESS: (id: string) => `/users/profile/addresses/${id}`,
    DELETE_ADDRESS: (id: string) => `/users/profile/addresses/${id}`,
    DELETE_ACCOUNT: '/users/profile/delete-account',
  },
  CHAT: {
    CONVERSATIONS: '/chat/conversations',
    CREATE_CONVERSATION: '/chat/conversations',
    MESSAGES: (conversationId: string) =>
      `/chat/conversations/${conversationId}/messages`,
    SEND_MESSAGE: '/chat/messages',
    MARK_AS_READ: (conversationId: string) =>
      `/chat/conversations/${conversationId}/read`,
    DELETE_CONVERSATION: (conversationId: string) =>
      `/chat/conversations/${conversationId}`,
  },
  NOTIFICATIONS: {
    GET_ALL: '/notifications',
    UNREAD_COUNT: '/notifications/unread-count',
    MARK_AS_READ: (id: string) => `/notifications/${id}/read`,
    MARK_ALL_AS_READ: '/notifications/read-all',
    DELETE: (id: string) => `/notifications/${id}`,
  },
  FAVORITES: {
    GET_ALL: '/favorites',
    ADD: '/favorites',
    REMOVE: (listingId: string) => `/favorites/${listingId}`,
    CHECK: (listingId: string) => `/favorites/${listingId}/check`,
    IDS: '/favorites/ids',
  },
  LOCATIONS: {
    GET_COUNTIES: '/locations/counties',
    GET_CITIES: '/locations/cities',
  },
  VEHICLE_REQUESTS: {
    SUBMIT: '/vehicle-requests',
    GET_MY_REQUESTS: '/vehicle-requests/my-requests',
    // Admin
    GET_QUEUE: '/admin/vehicle-requests',
    APPROVE: (id: string) => `/admin/vehicle-requests/${id}/approve`,
    REJECT: (id: string) => `/admin/vehicle-requests/${id}/reject`,
  },
  VEHICLE_MANAGEMENT: {
    // Makes
    MAKES: '/admin/vehicles/makes',
    DELETE_MAKE: (name: string) => `/admin/vehicles/makes/${encodeURIComponent(name)}`,
    // Models
    MODELS: '/admin/vehicles/models',
    // Features
    FEATURES: '/admin/vehicles/features',
    FEATURE_BY_ID: (id: number) => `/admin/vehicles/features/${id}`,
  },
  MARKETING: {
    // Public tracking + short-link resolution
    RESOLVE: (slug: string) => `/marketing/resolve/${encodeURIComponent(slug)}`,
    TRACK_VISIT: '/marketing/track/visit',
    TRACK_REDIRECT: '/marketing/track/redirect',
    // Admin — campaigns
    CAMPAIGNS: '/admin/marketing/campaigns',
    CAMPAIGN_BY_ID: (id: string) => `/admin/marketing/campaigns/${id}`,
    // Admin — analytics
    ANALYTICS_OVERVIEW: '/admin/marketing/analytics/overview',
    ANALYTICS_CAMPAIGNS: '/admin/marketing/analytics/campaigns',
    ANALYTICS_TIMESERIES: '/admin/marketing/analytics/timeseries',
  },
  GIVEAWAY: {
    // Admin — the giveaway shown on the app's profile screen
    GIVEAWAYS: '/admin/giveaways',
    GIVEAWAY_BY_ID: (id: string) => `/admin/giveaways/${id}`,
    STATS: (id: string) => `/admin/giveaways/${id}/stats`,
    ENTRIES: (id: string) => `/admin/giveaways/${id}/entries`,
    IMPORT_COMMENTS: (id: string) => `/admin/giveaways/${id}/import-comments`,
    DRAW: (id: string) => `/admin/giveaways/${id}/draw`,
    VERIFY_ENTRY: (entryId: string) =>
      `/admin/giveaways/entries/${entryId}/verify`,
    REJECT_ENTRY: (entryId: string) =>
      `/admin/giveaways/entries/${entryId}/reject`,
  },
  BULK_UPLOAD: {
    CSV_TEMPLATE: '/bulk-upload/csv-template',
    PARSE_CSV: '/bulk-upload/parse-csv',
    UPLOAD_IMAGES: (jobId: string) => `/bulk-upload/${jobId}/images`,
    SET_IMAGE_MAPPING: (jobId: string) => `/bulk-upload/${jobId}/image-mapping`,
    START_JOB: (jobId: string) => `/bulk-upload/${jobId}/start`,
    JOB_STATUS: (jobId: string) => `/bulk-upload/${jobId}/status`,
    MY_JOBS: '/bulk-upload/jobs',
    // Admin
    ADMIN_DEALERS: '/admin/bulk-upload/dealers',
    ADMIN_SINGLE_LISTING: '/admin/bulk-upload/listings/single',
  },
};
